import { useState, useEffect } from 'react';
import { X, Smartphone, MessageSquare, MessageCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { SendSmsModal } from './SendSmsModal';
import { JobContactConversationModal } from './JobContactConversationModal';
import { useBackNavigationLayer, navigateApp } from '@/lib/navigation';
import { useIsMobile } from '@/hooks/useIsMobile';
import { usePermissions } from '@/hooks/usePermissions';
import { canSendSmsFrom, stripPhoneToDigits } from '@/lib/phoneUtils';

interface SmsMethodSelectorProps {
    isOpen: boolean;
    onClose: () => void;
    recipientPhone: string;
    recipientName: string;
    recipientType: 'Technician' | 'Client';
    jobId?: string;
    /** Estimate SMS has no job; technicians pass this instead of jobId. */
    estimateId?: string;
    /** When true, hides native SMS and goes directly to the Service Strike VoIP SMS modal. */
    masked?: boolean;
    /** Contact ID for masked SMS — backend resolves real number server-side. */
    contactId?: string;
    /** Which phone number the opaque reference points to. Defaults to 'primary'. */
    contactWhich?: 'primary' | 'additional';
    /** The assigned technician's *user* id. When present (Technician recipient with a
     * linked user account), enables the "Internal messaging" option. */
    technicianUserId?: string;
}

/** Job contact SMS uses the conversation modal; resolve picks Telnyx/Twilio vs VoipMs. */
function hasJobContactContext(jobId?: string, contactId?: string): boolean {
    return !!(jobId && contactId);
}

export function SmsMethodSelector({
    isOpen,
    onClose,
    recipientPhone,
    recipientName,
    recipientType,
    jobId,
    estimateId,
    masked,
    contactId,
    contactWhich,
    technicianUserId,
}: SmsMethodSelectorProps) {
    const [showVoipSmsModal, setShowVoipSmsModal] = useState(false);
    const [showConversationModal, setShowConversationModal] = useState(false);
    const isMobile = useIsMobile();
    const { isFeatureAllowed } = usePermissions();

    // Plans without two-way SMS never open the conversation view — they fall back to the
    // single-send modal, which is what the templated invoice/estimate messages already use.
    const canUseConversations = isFeatureAllowed('messages.sms.custom');
    const canSendSms = isFeatureAllowed('messages.sms');
    const useJobContactConversation = hasJobContactContext(jobId, contactId) && canUseConversations;

    const handleSelectorClose = useBackNavigationLayer({
        enabled: isOpen && !showVoipSmsModal && !showConversationModal && !masked,
        onBack: onClose,
        state: { smsMethodSelector: true },
    });

    // Reset child modals when the selector closes so the next open starts clean
    useEffect(() => {
        if (!isOpen) {
            setShowVoipSmsModal(false);
            setShowConversationModal(false);
        }
    }, [isOpen]);

    // Auto-open the conversation path when masked (skip selector entirely)
    useEffect(() => {
        if (!isOpen || !masked || !useJobContactConversation) return;
        setShowConversationModal(true);
    }, [isOpen, masked, useJobContactConversation]);

    // Only offer "SMS via Service Strike" when an SMS-capable VoIP number exists.
    const { data: voipNumbers } = useQuery({
        queryKey: ['voip-phone-numbers'],
        queryFn: () => apiService.getPhoneNumbers(),
        enabled: isOpen && !masked,
    });
    // Don't hide the option before the numbers have loaded.
    const hasSmsNumber = canSendSms && (!voipNumbers || voipNumbers.some(canSendSmsFrom));
    const showInternalMessaging = recipientType === 'Technician' && !!technicianUserId && !masked
        && isFeatureAllowed('messages.internal');

    const handleInternalMessage = () => {
        navigateApp(`/messaging?internal_technician_id=${technicianUserId}`);
        onClose();
    };

    const handlePhoneSms = () => {
        const normalizedPhone = stripPhoneToDigits(recipientPhone);
        window.location.href = `sms:${normalizedPhone}`;
        onClose();
    };

    const handleServiceFalconSms = () => {
        if (useJobContactConversation) {
            setShowConversationModal(true);
        } else {
            setShowVoipSmsModal(true);
        }
    };

    const handleVoipModalClose = () => {
        setShowVoipSmsModal(false);
        setShowConversationModal(false);
        onClose();
    };

    if (!isOpen && !showVoipSmsModal && !showConversationModal) return null;

    return (
        <>
            {isOpen && !showVoipSmsModal && !showConversationModal && (
                <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[9000] p-0 sm:p-4">
                    <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-sm max-h-[calc(100dvh-0.75rem)] overflow-y-auto animate-in fade-in slide-in-from-bottom duration-200 safe-area-bottom-padded">
                        <div className="flex items-center justify-between p-4 border-b">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <MessageSquare className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900">Send SMS</h2>
                                    <p className="text-sm text-gray-500">To: {recipientName}</p>
                                </div>
                            </div>
                            <button
                                onClick={handleSelectorClose}
                                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                            >
                                <X className="h-5 w-5 text-gray-500" />
                            </button>
                        </div>

                        <div className="p-4 space-y-3">
                            <p className="text-sm text-gray-600 mb-4">Choose how to send the SMS:</p>

                            {!masked && isMobile && !!recipientPhone && (
                            <button
                                onClick={handlePhoneSms}
                                className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 transition-all group hover:border-blue-400 hover:bg-blue-50"
                            >
                                <div className="p-3 bg-green-100 rounded-xl group-hover:bg-green-200 transition-colors">
                                    <Smartphone className="h-6 w-6 text-green-600" />
                                </div>
                                <div className="text-left">
                                    <p className="font-semibold text-gray-900">SMS via Phone</p>
                                    <p className="text-sm text-gray-500">
                                        Open your phone's messaging app
                                    </p>
                                </div>
                            </button>
                            )}

                            {hasSmsNumber && (!!recipientPhone || useJobContactConversation) && (
                            <button
                                onClick={handleServiceFalconSms}
                                className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all group"
                            >
                                <div className="p-3 bg-blue-100 rounded-xl group-hover:bg-blue-200 transition-colors">
                                    <MessageSquare className="h-6 w-6 text-blue-600" />
                                </div>
                                <div className="text-left">
                                    <p className="font-semibold text-gray-900">SMS via Service Strike</p>
                                    <p className="text-sm text-gray-500">Send using your VoIP number</p>
                                </div>
                            </button>
                            )}

                            {showInternalMessaging && (
                            <button
                                onClick={handleInternalMessage}
                                className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all group"
                            >
                                <div className="p-3 bg-indigo-100 rounded-xl group-hover:bg-indigo-200 transition-colors">
                                    <MessageCircle className="h-6 w-6 text-indigo-600" />
                                </div>
                                <div className="text-left">
                                    <p className="font-semibold text-gray-900">Team chat</p>
                                    <p className="text-sm text-gray-500">Chat with the technician in the app</p>
                                </div>
                            </button>
                            )}
                        </div>

                        <div className="p-4 pt-0">
                            <button
                                onClick={handleSelectorClose}
                                className="w-full py-3 text-gray-600 font-medium hover:bg-gray-100 rounded-xl transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <SendSmsModal
                isOpen={showVoipSmsModal}
                onClose={handleVoipModalClose}
                recipientPhone={recipientPhone}
                recipientName={recipientName}
                recipientType={recipientType}
                jobId={jobId}
                estimateId={estimateId}
                toContactRef={masked && contactId ? `contact:${contactId}:${contactWhich ?? 'primary'}` : undefined}
            />

            {jobId && contactId && (
            <JobContactConversationModal
                isOpen={showConversationModal}
                onClose={handleVoipModalClose}
                jobId={jobId}
                contactId={contactId}
                which={contactWhich ?? 'primary'}
                masked={!!masked}
                contactName={recipientName}
                recipientPhone={recipientPhone}
            />
            )}
        </>
    );
}

export default SmsMethodSelector;

